import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { dateService } from '../../../domain/services/dateService';
import { t } from '../../../domain/services/configService';
import { SmartLink } from '../../SmartLink';
import { SponsoredTag } from '../../SponsoredTag';
import { Actionable } from '../atoms/Actionable';
import { IconPlus } from '../../Icon/IconPlus';
import { mergeDeep } from '../../../domain/utils/objectUtils';
import { IconGallery } from '../../Icon/IconGallery';
import { IconWatch } from '../../Icon/IconWatch';
import { IconExplore } from '../../Icon/IconExplore';
import { IconRead } from '../../Icon/IconRead';
import { theme } from '../../../domain/theme';

export const CardTextLegacy = ({
  url,
  target,
  title,
  subtitle,
  authors,
  date,
  videoDuration,
  sponsored,
  taxonomies,
  hover,
  styles,
  dataLayerClickEvent,
  ctaText,
  ctaType,
  ctaIcon,
  ctaButton
}) => {
  const [isOpen, setIsOpen] = useState(null);
  const handleMore = () => {
    setIsOpen(true);
  };
  const taxonomiesRef = useRef(null);
  useEffect(() => {
    if (taxonomiesRef.current) {
      if (
        taxonomiesRef.current.scrollHeight ===
        taxonomiesRef.current.clientHeight
      ) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    }
  }, []);
  const baseStyles = {
    title: { style: css`` },
    subtitle: { style: css`` },
    taxonomy: {
      margin: '15px 0',
      borderColorLeft: 'border-left: 1px solid #555',
      borderColorRight: 'border-right: 1px solid #555'
    }
  };
  const styleAttributes = mergeDeep(baseStyles, styles);
  const cardTextStyles = {
    headerTaxonomies:
      styleAttributes.taxonomy.headerTaxonomies ||
      css`
        ${theme.font.family('boldWeb')};
        ${styleAttributes.taxonomy?.headerTaxonomiesColor ||
        styleAttributes.color};
        display: inline-block;
        font-size: ${theme.font.size('xs')};
        text-decoration: none;
        line-height: ${theme.line.height('xl')};
        letter-spacing: 0.3em;
        margin: ${styleAttributes.taxonomy.margin};
        text-transform: uppercase;
        visibility: visible;
        span {
          /* ${styleAttributes.taxonomy.borderColorLeft}; */
          padding: 2px 10px 2px 0;
          /*           :last-child {
            ${styleAttributes.taxonomy.borderColorRight};
          } */
          :not(:first-of-type) {
            padding-left: 10px;
            ${styleAttributes.taxonomy.borderColorLeft};
          }
        }
        a {
          color: inherit;
          &:hover {
            text-decoration: underline;
          }
        }
      `,

    sponsoredElement: css`
      text-transform: uppercase;
      display: inline-block;
      margin-bottom: ${theme.spacing('xs3')};
    `,

    title: css`
      ${theme.font.family('boldWeb')};
      font-weight: 400;
      ${styleAttributes.color};
      /* This one is used when hovering over CardImage (so "hover" prop is true) */
      ${hover ? 'color: #999;' : `${styleAttributes.color};`}
      /* This one is used when hovering over the title, without any prop) */
      &:hover {
        color: #999;
      }
      font-size: 1.375rem;
      letter-spacing: 0.1px;
      line-height: 28px;
      margin-bottom: 15px;
      a {
        color: inherit;
        text-decoration: none;
      }
    `,

    subtitle: css`
      ${theme.font.family('regularWeb')};
      /* This one is used when hovering over CardImage (so "hover" prop is true) */
      ${hover ? 'color: #999;' : `${styleAttributes.color};`}
      /* This one is used when hovering over the title, without any prop) */
      &:hover {
        color: #999;
      }
      font-size: 1rem;
      letter-spacing: 0.1px;
      line-height: 24px;
      margin: 15px 0;
      a {
        color: inherit;
        text-decoration: none;
      }
    `,

    info: css`
      ${theme.font.family('regularWeb')};
      font-weight: 400;
      letter-spacing: 0.1px;
      line-height: 1.5;
      color: #999;
      font-size: 0.735rem;
      padding: 0 0 15px 0;
      span {
        color: #333;
      }
    `,

    taxonomies: css`
      display: flex;
      ${theme.font.family('boldWeb')};
      text-transform: uppercase;
      font-weight: 400;
      font-size: 11px;
      letter-spacing: 2.5px;
      line-height: 1.5;
      ul {
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        ${isOpen &&
        `overflow: unset;
        text-overflow: unset;
        display: block;
        -webkit-line-clamp: unset;
        -webkit-box-orient: unset;`}
        li {
          display: inline-block;
          margin: 5px 20px 0 0;
          a {
            ${styleAttributes.color};
          }
        }
      }
      a,
      button {
        display: inline-block;
        ${styleAttributes.color};
        border-bottom: 2px solid #fc0;
        background-image: linear-gradient(120deg, #fc0 0, #fc0 100%);
        background-repeat: no-repeat;
        background-size: 100% 0;
        background-position: 0 100%;
        transition: background-size 125ms;
        text-decoration: none;
        letter-spacing: 2.5px;
        line-height: 1.5;
        text-transform: uppercase;
        width: max-content;
        :hover {
          background-size: 100% 100%;
          svg {
            ${styleAttributes.color};
          }
        }
        svg {
          margin-left: 3px;
        }
      }
      ${styleAttributes.taxonomy?.footerTaxonomies};
    `,
    taxonomiesButtonWrapper: css`
      display: flex;
      align-items: flex-end;
      margin-left: 1px;
      ${isOpen === true && `visibility: hidden;`}
    `,
    authors: css`
      color: #333;
      a {
        color: inherit;
        :hover {
          color: #999;
        }
      }
    `,
    cta: css`
      text-align: ${ctaButton ? 'center' : 'left'};
      ${ctaButton && 'padding: 15px 0;'}
      max-width: 100%;
      overflow: hidden;
      overflow-wrap: anywhere;
      ${styleAttributes.cta || ''}
      a {
        ${styleAttributes.color};
        ${theme.font.family('boldWeb')};
        font-size: 12px;
        font-weight: 400;
        letter-spacing: 3px;
        text-transform: uppercase;
        line-height: 1.5;
        text-decoration: none;
        transition: 0.4s;
        padding: ${ctaButton ? '15px 30px' : '15px 0'};
        display: inline-block;
        background-color: ${ctaButton ? '#fc0' : 'transparent'};
        :hover {
          background-color: ${ctaButton ? '#fff' : 'transparent'};
        }
        svg {
          vertical-align: middle;
          margin: -2px 6px 0 0;
          ${styleAttributes.color};
        }
      }
    `
  };

  const ptt = taxonomies && taxonomies.find(term => term.isPtt);

  const headerItems = [];
  if (sponsored) {
    headerItems.push(
      <div css={cardTextStyles.sponsoredElement} key="sponsored">
        <SponsoredTag
          text={sponsored.text}
          bgColor={sponsored.bgColor}
          url={sponsored.url}
        />
      </div>
    );
  } else if (ptt && ptt.name) {
    headerItems.push(
      <span key={ptt.name}>
        {ptt.url ? <SmartLink to={ptt.url}>{ptt.name}</SmartLink> : ptt.name}
      </span>
    );
  }

  if (videoDuration) {
    headerItems.push(
      <span key="videoDuration">
        {dateService.secondsToMinutes(videoDuration)}
      </span>
    );
  }

  const taxonomyElements = taxonomies
    ? taxonomies
        .filter(term => {
          if (term.name && term.url) {
            if (term.isPtt) {
              return sponsored;
            }
            return true;
          }
          return false;
        })
        .map(term => (
          <li key={term.url}>
            <SmartLink to={term.url}>{term.name}</SmartLink>
          </li>
        ))
    : null;
  let IconComponent;
  switch (ctaType) {
    case 'watch':
      IconComponent = IconWatch;
      break;
    case 'gallery':
      IconComponent = IconGallery;
      break;
    case 'explore':
      IconComponent = IconExplore;
      break;
    case 'read':
      IconComponent = IconRead;
      break;
    default:
      IconComponent = !ctaText ? IconRead : null;
  }

  return (
    <>
      {headerItems.length > 0 && (
        <div css={cardTextStyles.headerTaxonomies}>
          {headerItems.map(headerItem => headerItem)}
        </div>
      )}

      <h2 css={[cardTextStyles.title, styleAttributes.title.style]}>
        <SmartLink
          to={url}
          target={target}
          title={title}
          dataLayerClickEvent={dataLayerClickEvent}
        >
          {title}
          {subtitle ? (
            <p css={[cardTextStyles.subtitle, styleAttributes.subtitle.style]}>
              {subtitle}
            </p>
          ) : null}
        </SmartLink>
      </h2>

      {(!!authors || !!date) && (
        <div css={cardTextStyles.info}>
          {date && dateService.format(date * 1000, 'd MM Y')}
          {!!authors && authors.length > 0 && (
            <>
              {!!date && '. '}
              {`${t('By')} `}

              {authors
                .map(author => {
                  const authorPageUrl =
                    author.numberOfPublishedNodes > 1 ? author.url : null;
                  return (
                    <span css={cardTextStyles.authors} key={author.name}>
                      {authorPageUrl ? (
                        <SmartLink to={authorPageUrl} key={author.name}>
                          {author.name}
                        </SmartLink>
                      ) : (
                        author.name
                      )}
                    </span>
                  );
                })
                .reduce((result, author) => [result, ', ', author])}
            </>
          )}
        </div>
      )}
      {taxonomyElements &&
        taxonomyElements.length > 0 &&
        !ctaType &&
        !ctaText && (
          <div css={cardTextStyles.taxonomies}>
            <ul ref={taxonomiesRef}>{taxonomyElements}</ul>
            <div css={cardTextStyles.taxonomiesButtonWrapper}>
              <Actionable title={t('__MORE__')} onClick={handleMore}>
                {t('__MORE__')}
                <IconPlus color="#fc0" size={10} />
              </Actionable>
            </div>
          </div>
        )}
      {(ctaType || ctaText) && (
        <div css={cardTextStyles.cta} className="card_cta">
          <SmartLink
            to={url}
            target={target}
            title={title}
            dataLayerClickEvent={dataLayerClickEvent}
          >
            {ctaIcon && IconComponent && (
              <IconComponent
                color={styleAttributes.color?.replace('color: ', '') || 'black'}
                size={18}
              />
            )}
            {ctaText || t(`__CTA_${ctaType.toUpperCase()}__`)}
          </SmartLink>
        </div>
      )}
    </>
  );
};

CardTextLegacy.defaultProps = {
  subtitle: undefined,
  authors: undefined,
  videoDuration: undefined,
  date: undefined,
  sponsored: undefined,
  taxonomies: undefined,
  hover: false,
  styles: {},
  dataLayerClickEvent: undefined,
  target: undefined,
  ctaType: undefined,
  ctaText: undefined,
  ctaIcon: true,
  ctaButton: false
};

CardTextLegacy.propTypes = {
  url: PropTypes.string.isRequired,
  target: PropTypes.string,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  authors: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      url: PropTypes.string,
      numberOfPublishedNodes: PropTypes.number
    }).isRequired
  ),
  date: PropTypes.number,
  videoDuration: PropTypes.number,
  sponsored: PropTypes.shape({
    text: PropTypes.string.isRequired,
    bgColor: PropTypes.string,
    url: PropTypes.string
  }),
  taxonomies: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string.isRequired,
      url: PropTypes.string,
      isPtt: PropTypes.bool
    })
  ),
  hover: PropTypes.bool,
  styles: PropTypes.shape({
    color: PropTypes.string,
    title: PropTypes.shape({
      style: PropTypes.objectOf(PropTypes.any)
    }),
    taxonomy: PropTypes.shape({
      headerTaxonomies: PropTypes.objectOf(PropTypes.any),
      margin: PropTypes.string,
      borderColorLeft: PropTypes.string,
      borderColorRight: PropTypes.string
    })
  }),
  dataLayerClickEvent: PropTypes.objectOf(PropTypes.any),
  ctaType: PropTypes.string,
  ctaText: PropTypes.string,
  ctaIcon: PropTypes.bool,
  ctaButton: PropTypes.bool
};
